import React from "react";
import { useBCMSContext, DisabledPrompt, BCMSValidate, ModalClose, StateManager, useStateManager, BCMSStateManagerCRUD, useWorkflow } from "bcms-react";
import { ContactInfo } from "model";

import { FormContainer, FormRowView, LeftColumnContainer, RightColumnContainer } from "components/shared";
import { HorizontalFormRow, FormButtonRow } from "components/shared/containers/FormContainer";
import UINotifiButton from "components/shared/notifi/UINotifiButton";
import UINotifiToggle from "components/shared/notifi/UINotifiToggle";
import { UITextInput } from "components/ui";
import { SVGAsset } from 'res';
import ProgressionBarWidget, { ProgressionStep } from "../ProgressionBarWidget";
import { LayoutContainer } from "components/shared";

import "./styles.css";
import"./mobile.css";

export default function WelcomeView(props) {
    const data = { ...getDefaultViewData(), ...props.data ?? {} };
    const ui = { ...getDefaultViewUI(), ...props.ui ?? {} };

    const { nextStep, state: workflow } = useWorkflow() ?? {};

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onUpdate: evt => {
            const updates = state.data.unconfirmedContacts.map(info => {
                if(info.type === "phone") {
                    const value = info.value.replaceAll(/[-() ]/g, "");
                    info.value = (BCMSValidate.isNumber(value) && value.length === 10) ? value : "(231) 256-7890";
                }
                else if(info.type === "email") {
                    info.value = BCMSValidate.isEmail(info.value) ? info.value : "sample@samplemail.com";
                }

                return info;
            }, []);

            const workflowState = BCMSStateManagerCRUD.init(workflow)
                .update([ "data", "unconfirmedContacts" ], updates)
                .submit();

            nextStep(workflowState);
        },

        onChange: (id, prop, value) => {
            const updates = state.data.unconfirmedContacts.map(info => {
                if(info.id === id) {
                    info[prop] = value;
                }

                return info;
            }, []);

            BCMSStateManagerCRUD.init(state)
                .update([ "data", "unconfirmedContacts" ], updates)
                .submit(setState);
        }
    });

    return (
        <StateManager state={ { data, ui } } controller={ controller }>
            <WelcomeContent />
        </StateManager>
    );
}

export const getDefaultViewData = () => ({
    unconfirmedContacts: []
});
export const getDefaultViewUI = () => ({});

function WelcomeContent(props) {
    const { data, onUpdate } = useStateManager();
    const { user } = useBCMSContext().state;

    const contactInfoInputRows = data.unconfirmedContacts.map(info => {
        if(info.userIsConfirmingContactInfo === undefined) {
            info.userIsConfirmingContactInfo = true;
        }

        return <ContactInfoInputRow key={ info.id } contactInfo={ info } />
    }, []);
    const contactsWithToggleOn = data.unconfirmedContacts.filter(info => info.userIsConfirmingContactInfo === true, []);

    return (
        <LayoutContainer>
          <LeftColumnContainer>
              <div className="flex-container justify-right close-container mobile-container">
                  <ModalClose id="AddContactMethodModal" className="clickable text-right">
                      <SVGAsset id="CloseX" />
                  </ModalClose>
              </div>
              {/* The progression bar ui is updated here as the user navigates through the workflow. */}
              
              <ProgressionBarWidget>
                  <ProgressionStep activeClass="active" />
                  <ProgressionStep />
              </ProgressionBarWidget>
    
              <div className="welcome-view">
                <div className="flex-container flex-vertical all-center header-container text-center">
                  <h1>Welcome to Notifications!</h1>
                  <p>To get started, make sure your contact info is correct.</p>
                </div>
                <FormContainer>
                  <HorizontalFormRow>
                    <FormRowView className="align-center flex-width input-container first-name">
                      <SVGAsset id="ProfileIcon" className="profile-icon" />
                      <UITextInput disabled={ true } value={ user.first } />
                    </FormRowView>
                    <FormRowView className="flex-width input-container last-name">
                      <UITextInput disabled={ true } value={ user.last } />
                    </FormRowView>
                  </HorizontalFormRow>
                  { contactInfoInputRows }
                  <FormButtonRow className="justify-right">
    
                  { contactsWithToggleOn.length > 0 &&
                    <UINotifiButton text="Next" className="justify-right clickable" onClick={ onUpdate } />
                  }
                  { contactsWithToggleOn.length < 1 &&
                    <div>
                      <DisabledPrompt>
                        <UINotifiButton text="Next" className="justify-right disabled" onClick={ onUpdate } />
                      </DisabledPrompt>
                    </div>
                  }
                  </FormButtonRow>
                </FormContainer>
              </div>
          </LeftColumnContainer>
    
          <RightColumnContainer>
              <div className="flex-container justify-right close-container">
                  <ModalClose id="AddContactMethodModal" className="clickable">
                      <SVGAsset id="CloseX" />
                  </ModalClose>
              </div>
              <h2>What are notifications?</h2>
              <p>Notifications are a great way to stay on top of important things happening with your profile and accounts.</p>
              <p>You can turn on the notifications that matter most to you and personalize them to meet your needs.</p>
              <h2>Tip</h2>
              <div className="flex-container">
                  <div className="set-width">
                      <SVGAsset id="InfoIcon" className="icon info-icon" />
                  </div>
                  <div className="flex-width">
                      <p>You can add or change your contact info later in notifications Settings.</p>
                  </div>
              </div>
          </RightColumnContainer>
      </LayoutContainer>
    );
}

function ContactInfoInputRow(props) {
    const { data, onChange } = useStateManager();
    const contactInfo = props.contactInfo ?? ContactInfo.firstInstanceOf();

    const toggle = value => onChange(contactInfo.id, "userIsConfirmingContactInfo", value);
    const updateUserEnteredPhoneNumber = value => onChange(contactInfo.id, "value", value);

    return (
        <HorizontalFormRow key={ contactInfo.id }>
          <FormRowView className="set-width align-center"  >
            <UINotifiToggle value={ contactInfo.userIsConfirmingContactInfo === true } onChange={ toggle } />
          </FormRowView>
          <FormRowView className="align-center flex-width input-container email">
            { contactInfo.type === "phone" &&
              <SVGAsset id="SquareMessageBubbleIcon" className="envelope-icon" />
            }
            { contactInfo.type === "email" &&
              <SVGAsset id="EnvelopeIcon" className="envelope-icon" />
            }
            <UITextInput value={ contactInfo.value } onChange={ updateUserEnteredPhoneNumber } />
          </FormRowView>
        </HorizontalFormRow>
    );
}